import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { todayIsoBrt } from '@/lib/dateBrt';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Plus,
  FileText,
  Calendar,
  BarChart3,
  PieChart,
  Edit3,
  Trash2,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  FileSpreadsheet,
  Bell
} from 'lucide-react';
import logger from '@/lib/logger';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';
import RevenueByTypeTreemap from '@/components/financial/RevenueByTypeTreemap';
import ImportExpensesModal from '@/components/financial/ImportExpensesModal';
import AlertsPanel from '@/components/financial/AlertsPanel';
import PeriodFilter, { getDashboardRange } from '@/components/financial/PeriodFilter';
import BreakEvenChart from '@/components/financial/BreakEvenChart';
import ProjectCombobox from '@/components/ProjectCombobox';
import { Upload, ArrowUpDown } from 'lucide-react';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend, Filler);

// Module-level chart options (stable references)
const SIMPLE_RESPONSIVE = { responsive: true, maintainAspectRatio: false };
const HORIZONTAL_BAR_OPTIONS = { ...SIMPLE_RESPONSIVE, indexAxis: 'y' };

function Financial({ user }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [entries, setEntries] = useState([]);
  const [cashFlow, setCashFlow] = useState(null);
  const [profitability, setProfitability] = useState([]);
  const [projects, setProjects] = useState([]);
  const [phases, setPhases] = useState([]);
  const [categories, setCategories] = useState({ revenue_categories: [], expense_categories: [] });
  const [loading, setLoading] = useState(true);
  const [alertCounts, setAlertCounts] = useState({ critical: 0, warning: 0, info: 0, total: 0 });

  // Dashboard period filter (KPIs become monthly/period-based)
  const [dashPeriod, setDashPeriod] = useState('thisMonth');
  const [dashCustomStart, setDashCustomStart] = useState('');
  const [dashCustomEnd, setDashCustomEnd] = useState('');

  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');   // YYYY-MM or 'all'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('due_date');        // 'due_date' | 'description'
  const [sortOrder, setSortOrder] = useState('asc');       // 'asc' | 'desc'

  // CSV import of expenses
  const [importExpensesOpen, setImportExpensesOpen] = useState(false);

  // Entry modal
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({
    entry_type: 'revenue',
    project_id: '',
    phase_id: '',
    category: '',
    description: '',
    amount: '',
    issue_date: '',
    due_date: '',
    payment_date: '',
    status: 'pending'
  });

  useEffect(() => {
    generateRecurringExpenses();
    fetchAllData();
    fetchAlertCounts();
  }, []);

  const fetchAlertCounts = async () => {
    try {
      const res = await api.get('/system-alerts');
      setAlertCounts(res.data?.counts || { critical: 0, warning: 0, info: 0, total: 0 });
    } catch (e) {
      // silently ignore — alerts are non-critical
    }
  };

  const generateRecurringExpenses = async () => {
    try {
      // Generate recurring expenses for current month (if not already generated)
      await api.post('/financial/generate-recurring');
    } catch (error) {
      // Silently ignore - expenses may already exist
      logger.log('Recurring expenses check completed');
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, entriesRes, cashFlowRes, profitRes, projectsRes, categoriesRes] = await Promise.all([
        api.get('/financial/dashboard'),
        api.get('/financial/entries'),
        api.get('/financial/cash-flow'),
        api.get('/financial/profitability'),
        api.get('/projects'),
        api.get('/financial/categories')
      ]);
      setDashboard(dashboardRes.data);
      setEntries(entriesRes.data);
      setCashFlow(cashFlowRes.data);
      setProfitability(profitRes.data);
      setProjects(projectsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      logger.error('Error fetching financial data:', error);
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };

  const fetchPhases = async (projectId) => {
    if (!projectId) {
      setPhases([]);
      return;
    }
    try {
      const response = await api.get(`/projects/${projectId}/phases`);
      setPhases(response.data);
    } catch (error) {
      logger.error('Error fetching phases:', error);
    }
  };

  const openNewEntry = (type = 'revenue') => {
    setEditingEntry(null);
    setEntryForm({
      entry_type: type,
      project_id: '',
      phase_id: '',
      category: '',
      description: '',
      amount: '',
      issue_date: todayIsoBrt(),
      due_date: '',
      payment_date: '',
      status: 'pending'
    });
    setPhases([]);
    setEntryModalOpen(true);
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setEntryForm({
      entry_type: entry.entry_type,
      project_id: entry.project_id,
      phase_id: entry.phase_id || '',
      category: entry.category,
      description: entry.description,
      amount: entry.amount.toString(),
      issue_date: entry.issue_date,
      due_date: entry.due_date,
      payment_date: entry.payment_date || '',
      status: entry.status
    });
    fetchPhases(entry.project_id);
    setEntryModalOpen(true);
  };

  const handleSaveEntry = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...entryForm,
        amount: parseFloat(entryForm.amount),
        phase_id: entryForm.phase_id || null,
        payment_date: entryForm.payment_date || null
      };

      if (editingEntry) {
        await api.put(`/financial/entries/${editingEntry.entry_id}`, data);
        toast.success('Entrada atualizada com sucesso!');
      } else {
        await api.post('/financial/entries', data);
        toast.success('Entrada criada com sucesso!');
      }
      setEntryModalOpen(false);
      fetchAllData();
    } catch (error) {
      logger.error('Error saving entry:', error);
      toast.error('Erro ao salvar entrada');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Deseja excluir esta entrada?')) return;
    try {
      await api.delete(`/financial/entries/${entryId}`);
      toast.success('Entrada excluída!');
      fetchAllData();
    } catch (error) {
      logger.error('Error deleting entry:', error);
      toast.error('Erro ao excluir entrada');
    }
  };

  const handleMarkPaid = async (entry) => {
    try {
      await api.put(`/financial/entries/${entry.entry_id}`, {
        status: 'paid',
        payment_date: todayIsoBrt()
      });
      toast.success('Marcado como pago!');
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  // Export functions
  const buildExportParams = () => {
    const params = new URLSearchParams();
    if (typeFilter !== 'all') params.append('entry_type', typeFilter);
    if (projectFilter !== 'all') params.append('project_id', projectFilter);
    if (statusFilter !== 'all') params.append('status', statusFilter);
    if (monthFilter !== 'all') params.append('month', monthFilter);
    if (categoryFilter !== 'all') params.append('category', categoryFilter);
    return params;
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Gerando planilha...');
      const params = buildExportParams();

      const response = await api.get(`/export/financial/excel?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = monthFilter !== 'all' ? monthFilter : todayIsoBrt();
      link.setAttribute('download', `relatorio_financeiro_${suffix}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Planilha exportada com sucesso!');
    } catch (error) {
      logger.error('Export error:', error);
      toast.error('Erro ao exportar planilha');
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Gerando PDF...');
      const params = buildExportParams();

      const response = await api.get(`/export/financial/pdf?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = monthFilter !== 'all' ? monthFilter : todayIsoBrt();
      link.setAttribute('download', `relatorio_financeiro_${suffix}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      logger.error('Export error:', error);
      toast.error('Erro ao exportar PDF');
    }
  };

  const handleExportProfitability = async () => {
    try {
      toast.info('Gerando relatório de lucratividade...');
      const response = await api.get('/export/profitability/excel', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lucratividade_${todayIsoBrt()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      logger.error('Export error:', error);
      toast.error('Erro ao exportar relatório');
    }
  };

  // Filter + sort entries for the Lançamentos tab
  const projectsById = useMemo(() => {
    const map = {};
    projects.forEach(p => { map[p.project_id] = p; });
    return map;
  }, [projects]);

  // Dashboard KPIs computed from entries[] filtered by the selected period (due_date)
  const dashboardKpis = useMemo(() => {
    const range = getDashboardRange(dashPeriod, dashCustomStart, dashCustomEnd);
    if (!range) {
      // "all" → fall back to backend totals
      return {
        revenue: dashboard?.kpis?.total_revenue || 0,
        expenses: dashboard?.kpis?.total_expenses || 0,
        revenue_paid: dashboard?.kpis?.total_revenue || 0,
        expenses_paid: dashboard?.kpis?.total_expenses || 0,
        revenue_pending: dashboard?.kpis?.revenue_pending || 0,
        expenses_pending: dashboard?.kpis?.expenses_pending || 0,
        revenue_overdue: dashboard?.kpis?.revenue_overdue || 0,
        expenses_overdue: dashboard?.kpis?.expenses_overdue || 0,
        net_profit: dashboard?.kpis?.net_profit || 0,
        profit_margin: dashboard?.kpis?.profit_margin || 0,
        scoped: false,
      };
    }
    const inRange = entries.filter(e => {
      const d = (e.due_date || '').slice(0, 10);
      return d && d >= range.start && d <= range.end;
    });
    const sum = (arr, predicate) =>
      arr.filter(predicate).reduce((s, e) => s + (Number(e.amount) || 0), 0);

    const revenueAll = sum(inRange, (e) => e.entry_type === 'revenue');
    const expensesAll = sum(inRange, (e) => e.entry_type === 'expense');
    const revenuePaid = sum(inRange, (e) => e.entry_type === 'revenue' && e.status === 'paid');
    const expensesPaid = sum(inRange, (e) => e.entry_type === 'expense' && e.status === 'paid');
    const revenuePending = sum(inRange, (e) => e.entry_type === 'revenue' && e.status === 'pending');
    const expensesPending = sum(inRange, (e) => e.entry_type === 'expense' && e.status === 'pending');
    const revenueOverdue = sum(inRange, (e) => e.entry_type === 'revenue' && e.status === 'overdue');
    const expensesOverdue = sum(inRange, (e) => e.entry_type === 'expense' && e.status === 'overdue');
    const net = revenueAll - expensesAll;
    const margin = revenueAll > 0 ? Math.round((net / revenueAll) * 1000) / 10 : 0;

    return {
      revenue: revenueAll,
      expenses: expensesAll,
      revenue_paid: revenuePaid,
      expenses_paid: expensesPaid,
      revenue_pending: revenuePending,
      expenses_pending: expensesPending,
      revenue_overdue: revenueOverdue,
      expenses_overdue: expensesOverdue,
      net_profit: net,
      profit_margin: margin,
      scoped: true,
      label: range.label,
    };
  }, [entries, dashPeriod, dashCustomStart, dashCustomEnd, dashboard]);

  const filteredEntries = (() => {
    const filtered = entries.filter(entry => {
      if (typeFilter !== 'all' && entry.entry_type !== typeFilter) return false;
      if (statusFilter !== 'all' && entry.status !== statusFilter) return false;
      if (projectFilter !== 'all' && entry.project_id !== projectFilter) return false;
      if (monthFilter !== 'all' && !(entry.due_date || '').startsWith(monthFilter)) return false;
      if (categoryFilter !== 'all' && entry.category !== categoryFilter) return false;
      return true;
    });
    const dir = sortOrder === 'asc' ? 1 : -1;
    return filtered.slice().sort((a, b) => {
      if (sortBy === 'description') {
        return (a.description || '').localeCompare(b.description || '', 'pt-BR') * dir;
      }
      // due_date (ISO string YYYY-MM-DD) — string compare works chronologically
      return ((a.due_date || '').localeCompare(b.due_date || '')) * dir;
    });
  })();

  // Month options dynamically derived from available entries + current year ±
  const availableMonths = (() => {
    const set = new Set();
    entries.forEach(e => {
      if (e.due_date) set.add(e.due_date.substring(0, 7));
    });
    return Array.from(set).sort().reverse();
  })();

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const sortIndicator = (column) => {
    if (sortBy !== column) return <ArrowUpDown className="w-3 h-3 inline opacity-40" />;
    return <span className="text-emerald-700 font-bold">{sortOrder === 'asc' ? '▲' : '▼'}</span>;
  };

  // Chart data
  const monthlyChartData = {
    labels: Object.keys(dashboard?.monthly_data || {}).map(m => {
      const [year, month] = m.split('-');
      return new Date(year, month - 1).toLocaleDateString('pt-BR', { month: 'short' });
    }),
    datasets: [
      {
        label: 'Receitas',
        data: Object.values(dashboard?.monthly_data || {}).map(d => d.revenue),
        backgroundColor: 'rgba(34, 197, 94, 0.7)',
        borderRadius: 4,
      },
      {
        label: 'Despesas',
        data: Object.values(dashboard?.monthly_data || {}).map(d => d.expenses),
        backgroundColor: 'rgba(239, 68, 68, 0.7)',
        borderRadius: 4,
      }
    ]
  };

  const cashFlowChartData = {
    labels: cashFlow?.forecast?.map(f => f.month_name.split(' ')[0]) || [],
    datasets: [
      {
        label: 'Saldo Acumulado',
        data: cashFlow?.forecast?.map(f => f.cumulative_balance) || [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.3,
      }
    ]
  };

  const profitabilityChartData = {
    labels: profitability.slice(0, 8).map(p => p.project_name.substring(0, 20)),
    datasets: [{
      label: 'Margem de Lucro (%)',
      data: profitability.slice(0, 8).map(p => p.profit_margin),
      backgroundColor: profitability.slice(0, 8).map(p => p.profit_margin >= 0 ? 'rgba(34, 197, 94, 0.7)' : 'rgba(239, 68, 68, 0.7)'),
      borderRadius: 4,
    }]
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  // Format ISO date (YYYY-MM-DD) as DD/MM/YYYY without timezone drift.
  // Using `new Date('2026-02-01')` parses as UTC midnight which in UTC-3
  // becomes 31/01 — that's the bug that made monthly filters look "mixed".
  const formatDateBR = (iso) => {
    if (!iso) return '';
    const s = String(iso).slice(0, 10);
    const parts = s.split('-');
    if (parts.length !== 3) return s;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const getStatusBadge = (status, type) => {
    if (status === 'paid') {
      return <Badge className="bg-green-100 text-green-700">Pago</Badge>;
    } else if (status === 'overdue') {
      return <Badge className="bg-red-100 text-red-700">Atrasado</Badge>;
    } else {
      return <Badge className="bg-yellow-100 text-yellow-700">Pendente</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Financeiro</h1>
            <p className="text-gray-600 mt-1">Gestão financeira e orçamentos</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Export buttons */}
            <Button onClick={handleExportExcel} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="export-excel-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel
            </Button>
            <Button onClick={handleExportPDF} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="export-pdf-btn">
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
            <div className="h-8 w-px bg-gray-300 mx-1 hidden sm:block"></div>
            <Button onClick={() => openNewEntry('revenue')} className="bg-green-600 hover:bg-green-700" data-testid="new-revenue-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nova Receita
            </Button>
            <Button onClick={() => openNewEntry('expense')} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="new-expense-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nova Despesa
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="entries" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Lançamentos
            </TabsTrigger>
            <TabsTrigger value="cashflow" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Fluxo de Caixa
            </TabsTrigger>
            <TabsTrigger value="profitability" className="flex items-center gap-2">
              <PieChart className="w-4 h-4" />
              Lucratividade
            </TabsTrigger>
            <TabsTrigger value="alerts" className="flex items-center gap-2" data-testid="tab-alerts">
              <Bell className="w-4 h-4" />
              Alertas
              {alertCounts.total > 0 && (
                <span
                  className={`ml-1 inline-flex items-center justify-center px-1.5 min-w-[20px] h-5 text-[10px] font-bold rounded-full ${
                    alertCounts.critical > 0
                      ? 'bg-red-600 text-white'
                      : alertCounts.warning > 0
                      ? 'bg-yellow-500 text-white'
                      : 'bg-blue-500 text-white'
                  }`}
                  data-testid="tab-alerts-badge"
                >
                  {alertCounts.total}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Period filter (KPIs computed for selected window) */}
            <PeriodFilter
              period={dashPeriod}
              onPeriodChange={setDashPeriod}
              customStart={dashCustomStart}
              customEnd={dashCustomEnd}
              onCustomStartChange={setDashCustomStart}
              onCustomEndChange={setDashCustomEnd}
            />

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500" data-testid="kpi-revenue-label">
                      {dashboardKpis.scoped ? 'Receitas do Período' : 'Receita Total'}
                    </p>
                    <p className="text-2xl font-bold text-green-600" data-testid="kpi-revenue-value">{formatCurrency(dashboardKpis.revenue)}</p>
                    {dashboardKpis.scoped && (
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        Pago: {formatCurrency(dashboardKpis.revenue_paid)}
                      </p>
                    )}
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg">
                    <ArrowUpRight className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500" data-testid="kpi-expenses-label">
                      {dashboardKpis.scoped ? 'Despesas do Período' : 'Despesas Total'}
                    </p>
                    <p className="text-2xl font-bold text-red-600" data-testid="kpi-expenses-value">{formatCurrency(dashboardKpis.expenses)}</p>
                    {dashboardKpis.scoped && (
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        Pago: {formatCurrency(dashboardKpis.expenses_paid)}
                      </p>
                    )}
                  </div>
                  <div className="p-3 bg-red-100 rounded-lg">
                    <ArrowDownRight className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">
                      {dashboardKpis.scoped ? 'Resultado do Período' : 'Lucro Líquido'}
                    </p>
                    <p className={`text-2xl font-bold ${dashboardKpis.net_profit >= 0 ? 'text-blue-600' : 'text-red-600'}`} data-testid="kpi-profit-value">
                      {formatCurrency(dashboardKpis.net_profit)}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${dashboardKpis.net_profit >= 0 ? 'bg-blue-100' : 'bg-red-100'}`}>
                    <DollarSign className={`w-6 h-6 ${dashboardKpis.net_profit >= 0 ? 'text-blue-600' : 'text-red-600'}`} />
                  </div>
                </div>
              </Card>

              <Card className="p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Margem de Lucro</p>
                    <p className={`text-2xl font-bold ${dashboardKpis.profit_margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="kpi-margin-value">
                      {dashboardKpis.profit_margin}%
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Pending/Overdue (always reflects the selected period) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-4 border border-yellow-200 bg-yellow-50">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-yellow-600" />
                  <div>
                    <p className="text-sm text-yellow-700">Receitas Pendentes</p>
                    <p className="text-xl font-bold text-yellow-800">{formatCurrency(dashboardKpis.revenue_pending)}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 border border-orange-200 bg-orange-50">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-orange-600" />
                  <div>
                    <p className="text-sm text-orange-700">Despesas Pendentes</p>
                    <p className="text-xl font-bold text-orange-800">{formatCurrency(dashboardKpis.expenses_pending)}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 border border-red-200 bg-red-50">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                  <div>
                    <p className="text-sm text-red-700">Pagamentos Atrasados</p>
                    <p className="text-xl font-bold text-red-800">
                      {formatCurrency((dashboardKpis.revenue_overdue || 0) + (dashboardKpis.expenses_overdue || 0))}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="p-6 border border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Receitas vs Despesas (Mensal)</h3>
                <div className="h-64">
                  <Bar data={monthlyChartData} options={SIMPLE_RESPONSIVE} />
                </div>
              </Card>

              <Card className="p-6 border border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Receitas por Tipo de Projeto</h3>
                <RevenueByTypeTreemap data={dashboard?.revenue_by_project_type || []} />
              </Card>
            </div>
          </TabsContent>

          {/* Entries Tab */}
          <TabsContent value="entries" className="space-y-4">
            {/* Filters */}
            <Card className="p-4 border border-gray-200">
              <div className="flex flex-wrap gap-3 items-center">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-36" data-testid="select-type-filter">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="revenue">Receitas</SelectItem>
                    <SelectItem value="expense">Despesas</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-36" data-testid="select-status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="paid">Pago</SelectItem>
                    <SelectItem value="overdue">Atrasado</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={projectFilter} onValueChange={setProjectFilter}>
                  <SelectTrigger className="w-52" data-testid="select-project-filter">
                    <SelectValue placeholder="Projeto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos Projetos</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>
                        {p.project_number ? `[${p.project_number}] ` : ''}{p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={monthFilter} onValueChange={setMonthFilter}>
                  <SelectTrigger className="w-44" data-testid="select-month-filter">
                    <SelectValue placeholder="Mês de Vencimento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os meses</SelectItem>
                    {availableMonths.map(m => {
                      const [y, mo] = m.split('-');
                      const label = new Date(parseInt(y), parseInt(mo) - 1)
                        .toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
                      return (
                        <SelectItem key={m} value={m}>
                          {label.charAt(0).toUpperCase() + label.slice(1)}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>

                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-48" data-testid="select-category-filter">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas categorias</SelectItem>
                    {(typeFilter === 'expense'
                      ? categories.expense_categories
                      : typeFilter === 'revenue'
                      ? categories.revenue_categories
                      : [...(categories.revenue_categories || []), ...(categories.expense_categories || [])]
                    ).map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex-1" />

                <Button
                  variant="outline"
                  onClick={() => setImportExpensesOpen(true)}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="btn-import-expenses-csv"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importar CSV
                </Button>
              </div>
              {(typeFilter !== 'all' || statusFilter !== 'all' || projectFilter !== 'all' || monthFilter !== 'all' || categoryFilter !== 'all') && (
                <div className="mt-2 text-xs text-gray-500">
                  Mostrando {filteredEntries.length} de {entries.length} lançamentos
                  <button
                    type="button"
                    className="ml-2 text-emerald-700 hover:underline"
                    onClick={() => {
                      setTypeFilter('all');
                      setStatusFilter('all');
                      setProjectFilter('all');
                      setMonthFilter('all');
                      setCategoryFilter('all');
                    }}
                  >
                    · limpar filtros
                  </button>
                </div>
              )}
            </Card>

            {/* Entries Table */}
            <Card className="border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Tipo</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        <button
                          type="button"
                          onClick={() => toggleSort('description')}
                          className="inline-flex items-center gap-1 hover:text-emerald-700"
                          data-testid="sort-description"
                        >
                          Descrição {sortIndicator('description')}
                        </button>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Projeto</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Categoria</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Valor</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">
                        <button
                          type="button"
                          onClick={() => toggleSort('due_date')}
                          className="inline-flex items-center gap-1 hover:text-emerald-700"
                          data-testid="sort-due-date"
                        >
                          Vencimento {sortIndicator('due_date')}
                        </button>
                      </th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Status</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredEntries.map(entry => (
                      <tr key={entry.entry_id} className="hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <Badge className={entry.entry_type === 'revenue' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {entry.entry_type === 'revenue' ? '📈 Receita' : '📉 Despesa'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-900">{entry.description}</td>
                        <td className="py-3 px-4 text-gray-600">
                          {entry.project_id && projectsById[entry.project_id]?.project_number && (
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 mr-1.5">
                              {projectsById[entry.project_id].project_number}
                            </span>
                          )}
                          {entry.project_name}
                        </td>
                        <td className="py-3 px-4 text-gray-600">{entry.category}</td>
                        <td className={`py-3 px-4 text-right font-bold ${entry.entry_type === 'revenue' ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(entry.amount)}
                        </td>
                        <td className="py-3 px-4 text-center text-gray-600">
                          {formatDateBR(entry.due_date)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {getStatusBadge(entry.status, entry.entry_type)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-1">
                            {entry.status !== 'paid' && (
                              <Button size="sm" variant="ghost" onClick={() => handleMarkPaid(entry)} title="Marcar como pago">
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => openEditEntry(entry)}>
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteEntry(entry.entry_id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredEntries.length === 0 && (
                  <p className="text-center py-8 text-gray-500">Nenhum lançamento encontrado</p>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Cash Flow Tab */}
          <TabsContent value="cashflow" className="space-y-6">
            {/* Break-even analysis */}
            <BreakEvenChart />

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-4 border border-gray-200">
                <p className="text-sm text-gray-500">Próximos 3 Meses</p>
                <p className={`text-2xl font-bold ${(cashFlow?.summary?.next_3_months || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(cashFlow?.summary?.next_3_months)}
                </p>
              </Card>
              <Card className="p-4 border border-gray-200">
                <p className="text-sm text-gray-500">Próximos 6 Meses</p>
                <p className={`text-2xl font-bold ${(cashFlow?.summary?.next_6_months || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(cashFlow?.summary?.next_6_months)}
                </p>
              </Card>
              <Card className="p-4 border border-gray-200">
                <p className="text-sm text-gray-500">Próximos 12 Meses</p>
                <p className={`text-2xl font-bold ${(cashFlow?.summary?.next_12_months || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(cashFlow?.summary?.next_12_months)}
                </p>
              </Card>
            </div>

            {/* Cash Flow Chart */}
            <Card className="p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Projeção de Fluxo de Caixa</h3>
              <div className="h-80">
                <Line data={cashFlowChartData} options={SIMPLE_RESPONSIVE} />
              </div>
            </Card>

            {/* Forecast Table */}
            <Card className="p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Previsão Detalhada</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold">Mês</th>
                      <th className="text-right py-3 px-4 font-semibold text-green-700">Receitas Previstas</th>
                      <th className="text-right py-3 px-4 font-semibold text-red-700">Despesas Previstas</th>
                      <th className="text-right py-3 px-4 font-semibold">Saldo do Mês</th>
                      <th className="text-right py-3 px-4 font-semibold">Saldo Acumulado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {cashFlow?.forecast?.map((f, i) => (
                      <tr key={f.month} className={i === 0 ? 'bg-blue-50' : ''}>
                        <td className="py-3 px-4 font-medium">{f.month_name}</td>
                        <td className="py-3 px-4 text-right text-green-600">{formatCurrency(f.expected_revenue)}</td>
                        <td className="py-3 px-4 text-right text-red-600">{formatCurrency(f.expected_expenses)}</td>
                        <td className={`py-3 px-4 text-right font-bold ${f.forecast_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(f.forecast_balance)}
                        </td>
                        <td className={`py-3 px-4 text-right font-bold ${f.cumulative_balance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          {formatCurrency(f.cumulative_balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Profitability Tab */}
          <TabsContent value="profitability" className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Lucratividade por Projeto</h3>
              <Button onClick={handleExportProfitability} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="export-profitability-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Exportar Excel
              </Button>
            </div>
            
            <Card className="p-6 border border-gray-200">
              <div className="h-80">
                <Bar data={profitabilityChartData} options={HORIZONTAL_BAR_OPTIONS} />
              </div>
            </Card>

            <Card className="border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold">Projeto</th>
                      <th className="text-left py-3 px-4 font-semibold">Cliente</th>
                      <th className="text-right py-3 px-4 font-semibold text-green-700">Receita</th>
                      <th className="text-right py-3 px-4 font-semibold text-red-700">Despesas</th>
                      <th className="text-right py-3 px-4 font-semibold">Lucro</th>
                      <th className="text-center py-3 px-4 font-semibold">Margem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {profitability.map(p => (
                      <tr key={p.project_id} className="hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium text-gray-900">{p.project_name}</td>
                        <td className="py-3 px-4 text-gray-600">{p.client_name}</td>
                        <td className="py-3 px-4 text-right text-green-600">{formatCurrency(p.revenue)}</td>
                        <td className="py-3 px-4 text-right text-red-600">{formatCurrency(p.expenses)}</td>
                        <td className={`py-3 px-4 text-right font-bold ${p.profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          {formatCurrency(p.profit)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={p.profit_margin >= 30 ? 'bg-green-100 text-green-700' : p.profit_margin >= 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>
                            {p.profit_margin}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {profitability.length === 0 && (
                  <p className="text-center py-8 text-gray-500">Nenhum dado de lucratividade disponível</p>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="space-y-4">
            <AlertsPanel />
          </TabsContent>
        </Tabs>
      </div>

      {/* Entry Modal */}
      <Dialog open={entryModalOpen} onOpenChange={setEntryModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? 'Editar Lançamento' : `Nova ${entryForm.entry_type === 'revenue' ? 'Receita' : 'Despesa'}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEntry} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select value={entryForm.entry_type} onValueChange={(v) => setEntryForm({...entryForm, entry_type: v, category: ''})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">Receita</SelectItem>
                    <SelectItem value="expense">Despesa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoria</Label>
                <Select value={entryForm.category} onValueChange={(v) => setEntryForm({...entryForm, category: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {(entryForm.entry_type === 'revenue' ? categories.revenue_categories : categories.expense_categories).map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Projeto {entryForm.entry_type === 'expense' ? '(opcional)' : ''}</Label>
              <ProjectCombobox
                projects={projects}
                value={entryForm.project_id}
                onChange={(projectId) => {
                  setEntryForm({...entryForm, project_id: projectId, phase_id: ''});
                  if (projectId) fetchPhases(projectId);
                }}
                allowEmpty={entryForm.entry_type === 'expense'}
                emptyLabel="Sem projeto vinculado"
                placeholder="Selecione o projeto"
                testId="financial-project-combobox"
              />
            </div>

            {phases.length > 0 && entryForm.project_id && (
              <div>
                <Label>Fase (opcional)</Label>
                <Select value={entryForm.phase_id || 'none'} onValueChange={(v) => setEntryForm({...entryForm, phase_id: v === 'none' ? '' : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a fase" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {phases.map(ph => (
                      <SelectItem key={ph.phase_id} value={ph.phase_id}>{ph.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Descrição</Label>
              <Input
                value={entryForm.description}
                onChange={(e) => setEntryForm({...entryForm, description: e.target.value})}
                placeholder="Descrição do lançamento"
                required
              />
            </div>

            <div>
              <Label>Valor (R$)</Label>
              <Input
                type="number"
                step="0.01"
                value={entryForm.amount}
                onChange={(e) => setEntryForm({...entryForm, amount: e.target.value})}
                placeholder="0,00"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Emissão</Label>
                <Input
                  type="date"
                  value={entryForm.issue_date}
                  onChange={(e) => setEntryForm({...entryForm, issue_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Data Vencimento</Label>
                <Input
                  type="date"
                  value={entryForm.due_date}
                  onChange={(e) => setEntryForm({...entryForm, due_date: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Pagamento</Label>
                <Input
                  type="date"
                  value={entryForm.payment_date}
                  onChange={(e) => setEntryForm({...entryForm, payment_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={entryForm.status} onValueChange={(v) => setEntryForm({...entryForm, status: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="paid">Pago</SelectItem>
                    <SelectItem value="overdue">Atrasado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEntryModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
                Salvar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ImportExpensesModal
        open={importExpensesOpen}
        onOpenChange={setImportExpensesOpen}
        onImported={fetchAllData}
      />
    </Layout>
  );
}

export default Financial;
